import { Injectable } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';

import { ScanProcessingService } from '@mirage-projects/cw-commons';
import {
    PcvizScanProcessingOptions,
    PcvizWorkspaceController,
    PcvizWorkspaceScan,
    PcvizWorkspaceStationElement,
    PcvizWorkspaceStationTypes,
    PcvizWorkspaceStationTypeValues,
} from '@mirage-projects/cw-models';
import { DbPcvizService, GisPcvizService } from '@mirage-projects/cw-pcviz-core';

/**
 * This service holds the data for the point selector step used in the create wizard
 */
@Injectable()
export class ScanProcessingOptionsService {
    scanProcessingForm!: FormGroup;

    readonly CRP1_MAX_DISTANCE = 999;
    readonly DEFAULT_MAX_DISTANCE = 1000;

    constructor(
        private formBuilder: FormBuilder,
        private scanProcessing: ScanProcessingService,
        private gisPcvizService: GisPcvizService,
        private dbPcvizService: DbPcvizService
    ) {}

    initScanProcessingForm(options: PcvizScanProcessingOptions): void {
        this.scanProcessingForm = this.formBuilder.group({
            processPointcloud: [options.processPointcloud],
            highQualityPointcloud: [options.highQualityPointcloud],
            precision: [options.precision],
            density: [options.density],
            maxDistanceEnabled: [true],
            maxDistanceValue: [options.maxDistance, Validators.required],
            edgeNoiseReduction: [options.edgeNoiseReduction],
            edgeNoiseReductionLevel: [options.edgeNoiseReductionLevel],
            surfaceSmoothing: [options.surfaceSmoothing],
            strayFilter: [options.strayFilter],
            autodetectTargets: [options.autodetectTargets],
            colorize: [options.colorize],
            processPanorama: [options.processPanorama],
            resolution: [this.scanProcessing.panoramaResolution[0].value],
            radius: [options.panoramaRadius],
            scanProcessingForm: undefined,
            autodetectSpheres: [options.autodetectSpheres],
            autodetectCheckerboards: [options.autodetectCheckerboards],
        });
    }

    getSelectedScanTypes(scans: PcvizWorkspaceScan[]): Set<PcvizWorkspaceStationTypeValues> {
        const selectedScans = scans.filter((scan) => scan.selected);
        const selectedScansUUID = selectedScans.map((scan) => scan.uuid);
        const workspaceController = this.dbPcvizService.getInstanceDB().getController('Workspace') as PcvizWorkspaceController;
        const workspace = workspaceController.getWorkspace();

        const workspaceVisitor = this.gisPcvizService.createWorkspaceVisitor();
        workspaceVisitor.traverse(workspace.workspace);
        const stations: PcvizWorkspaceStationElement[] = workspaceVisitor.getStations() ?? [];
        const stationsWithSelectedScans = stations.filter((station) => {
            const scanFolder = station.children.find((child) => child.category === 'ScansFolder');
            const hasSelectedScans = scanFolder?.children?.some((scan) => selectedScansUUID.includes(scan.uuid));
            return hasSelectedScans;
        });
        const selectedScanTypes = stationsWithSelectedScans.map((station) => station.properties.type);
        return new Set(selectedScanTypes);
    }

    get workspaceStationTypes(): PcvizWorkspaceStationTypes {
        return this.gisPcvizService.getWorkspaceStationTypes();
    }
}
